<script lang="ts">
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { bulkUpdate } from './app_settings_helper';
	import { onSettingsResponse } from '../../../helpers/device_settings_helpers';
	import {
		flagsToSyncSelection,
		syncSelectionToFlags,
		type NameSyncTarget,
	} from 'src/helpers/device_name_sync_helpers';
	//Stores
	import { devices } from 'src/stores/lists/devices';
	//Services
	import { postReq } from 'src/services/http';
	//Design system components
	import { Container, Radio, Toggle } from '@Shelly/shelly-design-system';
	//Types
	import type { Device } from '../../../types/Device';
	import type { InputClickEvent } from '../../../types/InputClickEvent';

	export let device: Device;
	/** 'toggle' for Gen1 / DeviceNameSys / multi-channel; 'radios' for single-channel G2 merged UI */
	export let variant: 'toggle' | 'radios' = 'toggle';
	/** When variant is radios, whether to offer syncing to sys.device.name */
	export let allowSysTarget = false;
	/** Current system name — used to align app name when switching target to sys */
	export let sysName = '';
	/** Current component/channel name — used when switching target to channel */
	export let channelName = '';
	export let nameType: 'device' | 'channel' = 'device';
	/** When enabling toggle sync, which target to persist (sys for DeviceNameSys). */
	export let defaultTarget: NameSyncTarget = 'channel';
	export let info: string = '';

	const id = device.id;
	let checked = $devices[id]?.name_sync || false;
	let selected: 'off' | NameSyncTarget = flagsToSyncSelection($devices[id] ?? device);
	let waitForResponse = false;

	$: storeDevice = $devices[id];
	$: if (storeDevice && variant === 'radios' && !waitForResponse) {
		selected = flagsToSyncSelection(storeDevice);
	}
	$: if (storeDevice && variant === 'toggle' && !waitForResponse) {
		checked = storeDevice.name_sync || false;
	}

	/**
	 * Persist sync flags and optionally align the app display name to the chosen target.
	 * @param showToast - whether to show the enabled/disabled success toast (only when the
	 *   relevant system/component name has a value)
	 */
	const persistSync = async (
		name_sync: boolean,
		name_sync_target: NameSyncTarget | undefined,
		alignAppName: string | undefined,
		showToast = true,
	) => {
		waitForResponse = true;
		const payload: Record<string, unknown> = { name_sync };
		if (name_sync && name_sync_target) {
			payload.name_sync_target = name_sync_target;
		}

		const res = await bulkUpdate(id, payload);
		let toastTexts = {};

		if (res.isok) {
			const updated: Device = {
				...($devices[id] ?? device),
				name_sync,
			};

			if (name_sync && name_sync_target) {
				updated.name_sync_target = name_sync_target;
			} else {
				delete updated.name_sync_target;
			}

			if (alignAppName && alignAppName.trim() !== '' && updated.name !== alignAppName) {
				updated.name = alignAppName.trim();
				await postReq('interface/device/save', {
					id,
					data: JSON.stringify(updated),
				});
			}

			devices.update(updated);

			if (showToast) {
				toastTexts = {
					success: name_sync
						? $format('sync_name_enabled_scc_toast', {
								default: 'Name sync is enabled!',
							})
						: $format('sync_name_disabled_scc_toast', {
								default: 'Name sync is disabled!',
							}),
				};
			}
		} else {
			if (variant === 'radios') {
				selected = flagsToSyncSelection($devices[id] ?? device);
			} else {
				checked = !checked;
			}
		}

		const shouldToast = res.isok ? Object.keys(toastTexts).length > 0 : true;
		onSettingsResponse(res, device, toastTexts, shouldToast);
		waitForResponse = false;
	};

	const saveToggle = async (e: InputClickEvent) => {
		checked = e.target.checked;
		const name_sync = checked;
		await persistSync(name_sync, name_sync ? defaultTarget : undefined, undefined, true);
	};

	const saveRadios = async (next: 'off' | NameSyncTarget) => {
		const previous = flagsToSyncSelection($devices[id] ?? device);
		if (next === previous || waitForResponse) return;

		selected = next;
		const { name_sync, name_sync_target } = syncSelectionToFlags(next);
		let alignAppName: string | undefined;
		let showToast = false;

		if (name_sync && name_sync_target === 'sys') {
			alignAppName = sysName;
			// Enabled toast only when turning sync on from "Do not sync"
			showToast = previous === 'off' && typeof sysName === 'string' && sysName.trim() !== '';
		} else if (name_sync && name_sync_target === 'channel') {
			alignAppName = channelName;
			showToast =
				previous === 'off' && typeof channelName === 'string' && channelName.trim() !== '';
		} else if (!name_sync) {
			// Disabled toast only when leaving a sync target that actually had a name
			if (previous === 'sys') {
				showToast = typeof sysName === 'string' && sysName.trim() !== '';
			} else if (previous === 'channel') {
				showToast = typeof channelName === 'string' && channelName.trim() !== '';
			}
		}

		await persistSync(name_sync, name_sync_target, alignAppName, showToast);
	};
</script>

{#if variant === 'radios'}
	<div
		class="mb-s"
		role="radiogroup"
		aria-label={$format('sync_device_name', { default: 'Sync device name' })}
	>
		<Radio
			size="m"
			bind:selected
			value="off"
			id="radio-name-sync-off"
			disabled={waitForResponse}
			info={$format('name_sync_off_info', {
				default:
					'The app name stays independent from the device. Use the field under this option to change only the name shown in the app.',
			})}
			on:click={() => saveRadios('off')}
		>
			{$format('name_sync_option_off', { default: 'Do not sync' })}
		</Radio>
		{#if selected === 'off'}
			<div class="mt-s pl-xs pr-xs pb-xs pt-xs">
				<Container type="borderSolid" padding="s">
					<slot name="panel-off" />
				</Container>
			</div>
		{/if}

		{#if allowSysTarget}
			<Radio
				size="m"
				bind:selected
				value="sys"
				id="radio-name-sync-sys"
				disabled={waitForResponse}
				info={$format('name_sync_sys_info', {
					default:
						'Keep the app name in sync with the device system name. Changes in the app or on the device update each other.',
				})}
				on:click={() => saveRadios('sys')}
			>
				{$format('name_sync_option_sys', { default: 'Sync with system name' })}
			</Radio>
			{#if selected === 'sys'}
				<div class="mt-s pl-xs pr-xs pb-xs pt-xs">
					<Container type="borderSolid" padding="s">
						<slot name="panel-sys" />
					</Container>
				</div>
			{/if}
		{/if}

		<Radio
			size="m"
			bind:selected
			value="channel"
			id="radio-name-sync-channel"
			disabled={waitForResponse}
			info={$format('name_sync_channel_info', {
				default:
					'Keep the app name in sync with the component (channel) name. Changes in the app or on the device update each other.',
			})}
			on:click={() => saveRadios('channel')}
		>
			{$format('name_sync_option_channel', { default: 'Sync with component name' })}
		</Radio>
		{#if selected === 'channel'}
			<div class="mt-s pl-xs pr-xs pb-xs pt-xs">
				<Container type="borderSolid" padding="s">
					<slot name="panel-channel" />
				</Container>
			</div>
		{/if}
	</div>
{:else}
	<!-- 'sync name' will toggle the option that update device name in the interface data
	and the setting data. Which means if the device has only one channel 
	and only device name option the sync name toggle will be in that collapsable card.
	If device has multiple channels and has channel and device name 
	the toggle option will be in channel name collapse card,
	because channel name update the name of the interface data of the device 
	and local settings of the channel. On the other hand the device name option 
	update device name by default in the settings -->

	<Toggle
		size="m"
		disabled={waitForResponse}
		bind:checked
		on:click={saveToggle}
		id="toggle-1hh77ki"
		{info}
	>
		{nameType === 'device'
			? $format('sync_device_name', {
					default: 'Sync device name',
				})
			: $format('sync_channel_name', {
					default: 'Sync channel name',
				})}
	</Toggle>
{/if}
